
import { TimelineItem } from "../shared/timeline.js";
import { AppToolActivity } from "./app-tool-activity.js";
import { IconChevronDown } from "./icons/chevron-down.js";

const styles = css`
  :host { display: block; min-width: 0; }
  .tools { display: flex; flex-direction: column; gap: 1px; }
  .toggle { align-items: center; background: transparent; border: 0; border-radius: var(--radius-sm); color: color-mix(in oklab, var(--foreground) 82%, transparent); cursor: pointer; display: flex; font: inherit; font-size: 0.75rem; font-weight: 500; gap: 6px; line-height: 20px; padding: 2px; text-align: left; width: 100%; }
  .toggle:hover { background: color-mix(in oklab, var(--accent) 20%, transparent); }
  .toggle:focus-visible { box-shadow: 0 0 0 2px var(--ring-shadow); outline: none; }
  .toggle-icon { align-items: center; color: color-mix(in oklab, var(--muted-foreground) 65%, transparent); display: inline-flex; height: 20px; justify-content: center; width: 20px; }
  .toggle-icon > * { height: 14px; transition: transform 160ms ease; width: 14px; }
  .toggle-icon.expanded > * { transform: rotate(180deg); }
  @media (prefers-reduced-motion: reduce) { .toggle-icon > * { transition: none; } }
`;

export class AppToolCluster extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof AppToolCluster)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = ["tools","data-tools"];

  #tools = [];
  #previousVisible;
  #toolsSet = false;
  #textStart0;
  #textEnd0;
  #textLast0;
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  get tools() {
    return this.#tools;
  }

  set tools(value) {
    const raw = value ?? [];
    const next = raw == null ? raw : typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Object.is(this.#tools, next)) return;
    this.#tools = next;
    this.#toolsSet = true;
    this.#updateBindings('tools');
  }

  #setPreviousVisible(value) {
    if (Object.is(this.#previousVisible, value)) return;
    this.#previousVisible = value;
    this.#updateBindings('previousVisible');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;
    if (this.hasAttribute('data-tools')) this.tools = this.dataset.tools;
  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;
      this.tools = props.tools;
      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (this.#isCSR || this.#bindingsMounted) return;
    this.#syncHydratedProps();
    if (!this.#toolsSet) this.tools = this.dataset.tools;
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'tools':
      case 'data-tools':
        this.tools = newValue;
        break;
    }
  }

  #initState() {
    this.#previousVisible = false;
  }

  #render() {
    const previous = this.#tools.slice(0, -1);
    const newest = this.#tools.slice(-1);
    return `<style>${this.#escText(styles)}</style><div class="tools"><!--argon-text:0-->${this.#previousVisible ? previous.map((tool) => `<app-tool-activity data-activity-id="${this.#attrEsc(tool.id)}" data-seq="${this.#attrEsc(tool.seq)}" data-title="${this.#attrEsc((tool.toolName !== "") ? tool.toolName : "Tool output")}" data-detail="${this.#attrEsc(tool.toolDetail)}" data-content="${this.#attrEsc(tool.text)}" data-status="${this.#attrEsc(tool.status)}" data-is-error="${this.#attrEsc(tool.isError)}" data-subagent-key="${this.#attrEsc(tool.subagentKey ?? "")}">${""}</app-tool-activity>`).join("") : ""}${newest.map((tool) => `<app-tool-activity data-activity-id="${this.#attrEsc(tool.id)}" data-seq="${this.#attrEsc(tool.seq)}" data-title="${this.#attrEsc((tool.toolName !== "") ? tool.toolName : "Tool output")}" data-detail="${this.#attrEsc(tool.toolDetail)}" data-content="${this.#attrEsc(tool.text)}" data-status="${this.#attrEsc(tool.status)}" data-is-error="${this.#attrEsc(tool.isError)}" data-subagent-key="${this.#attrEsc(tool.subagentKey ?? "")}">${""}</app-tool-activity>`).join("")}${(previous.length > 0) ? `<button class="toggle" type="button" aria-expanded="${this.#escAttr(this.#previousVisible ? "true" : "false")}" data-argon="2244"><span class="${this.#escAttr(`toggle-icon${this.#previousVisible ? " expanded" : ""}`)}" aria-hidden="true"><icon-chevron-down>${""}</icon-chevron-down></span><span>${this.#escText(this.#previousVisible ? "Show fewer tool calls" : `+${previous.length} previous tool call${(previous.length === 1) ? "" : "s"}`)}</span></button>` : ""}<!--/argon-text:0--></div>`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
      }
    }
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }
  #attrRaw(v) {
    return typeof v === 'object' ? JSON.stringify(v) : String(v);
  }

  #attrEsc(v) {
    return this.#attrRaw(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
  }

  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  #escAttr(v) {
    return String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }


  #eventTargets(scope, id) {
    const selector = `[data-argon="${id}"]`;
    const targets = [...scope.querySelectorAll(selector)];
    if (scope.nodeType === 1 && scope.matches(selector)) targets.unshift(scope);
    return targets;
  }

  #listen(el, id, event, handler) {
    const key = id + ':' + event;
    const root = this.#root;
    if (!root.__argonListeners) Object.defineProperty(root, '__argonListeners', { value: {} });
    const prev = root.__argonListeners[key];
    if (prev) root.removeEventListener(event, prev, true);
    const next = event => {
      const target = event.composedPath().find(node => node?.getAttribute?.('data-argon') === id);
      if (!target) return;
      const delegated = new Proxy(event, { get(source, prop) {
        if (prop === 'currentTarget') return target;
        const value = Reflect.get(source, prop, source);
        return typeof value === 'function' ? value.bind(source) : value;
      } });
      handler.call(target, delegated);
    };
    root.__argonListeners[key] = next;
    root.addEventListener(event, next, true);
  }

  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    const previous = this.#tools.slice(0, -1);
    const newest = this.#tools.slice(-1);
    if (prop === undefined || prop === 'previousVisible' || prop === 'tools') {
      const __argonNext = `${this.#previousVisible ? previous.map((tool) => `<app-tool-activity data-activity-id="${this.#attrEsc(tool.id)}" data-seq="${this.#attrEsc(tool.seq)}" data-title="${this.#attrEsc((tool.toolName !== "") ? tool.toolName : "Tool output")}" data-detail="${this.#attrEsc(tool.toolDetail)}" data-content="${this.#attrEsc(tool.text)}" data-status="${this.#attrEsc(tool.status)}" data-is-error="${this.#attrEsc(tool.isError)}" data-subagent-key="${this.#attrEsc(tool.subagentKey ?? "")}">${""}</app-tool-activity>`).join("") : ""}${newest.map((tool) => `<app-tool-activity data-activity-id="${this.#attrEsc(tool.id)}" data-seq="${this.#attrEsc(tool.seq)}" data-title="${this.#attrEsc((tool.toolName !== "") ? tool.toolName : "Tool output")}" data-detail="${this.#attrEsc(tool.toolDetail)}" data-content="${this.#attrEsc(tool.text)}" data-status="${this.#attrEsc(tool.status)}" data-is-error="${this.#attrEsc(tool.isError)}" data-subagent-key="${this.#attrEsc(tool.subagentKey ?? "")}">${""}</app-tool-activity>`).join("")}${(previous.length > 0) ? `<button class="toggle" type="button" aria-expanded="${this.#escAttr(this.#previousVisible ? "true" : "false")}" data-argon="2244"><span class="${this.#escAttr(`toggle-icon${this.#previousVisible ? " expanded" : ""}`)}" aria-hidden="true"><icon-chevron-down>${""}</icon-chevron-down></span><span>${this.#escText(this.#previousVisible ? "Show fewer tool calls" : `+${previous.length} previous tool call${(previous.length === 1) ? "" : "s"}`)}</span></button>` : ""}`;
      if (__argonNext !== this.#textLast0) {
        this.#textLast0 = __argonNext;
        this.#replaceRange(this.#textStart0, this.#textEnd0, __argonNext);
      }
    }
  }

  #wireEvents(scope) {
    const root = this.#root;
    this.#eventTargets(scope, '2244').forEach(el => this.#listen(el, '2244', 'click', () => { this.#setPreviousVisible(!this.#previousVisible); }));
    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
